import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = ({ t }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    huntType: '',
    dates: '',
    guests: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t.contact.form.success);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        huntType: '',
        dates: '',
        guests: '',
        message: ''
      });
    } catch (err) {
      console.error('Contact submit error:', err);
      toast.error(t.contact.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.contact.title}
          </h2>
          <p className="text-xl text-orange-500 font-semibold">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-stone-900 border border-orange-500/20 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-6">
                {t.contact.info.title}
              </h3>

              <div className="space-y-6">
                <div className="flex items-start">
                  <MapPin className="h-6 w-6 text-orange-500 mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Adresse</p>
                    <p className="text-white">{t.contact.info.address}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="h-6 w-6 text-orange-500 mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Téléphone</p>
                    <p className="text-white">{t.contact.info.phone}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="h-6 w-6 text-orange-500 mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Email</p>
                    <p className="text-white">{t.contact.info.email}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-orange-500 mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Heures d'ouverture</p>
                    <p className="text-white">{t.contact.info.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-stone-900 border border-orange-500/20 rounded-lg overflow-hidden h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2800846.6725058146!2d-77.5!3d46.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cce0718cc4a6ad7%3A0x3a7e3ace5242693d!2sOutaouais%2C%20QC!5e0!3m2!1sen!2sca!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Location Map"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-stone-900 border border-orange-500/20 rounded-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-white mb-2 block">
                    {t.contact.form.firstName}
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="bg-stone-800 border-orange-500/30 text-white focus:border-orange-500"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-white mb-2 block">
                    {t.contact.form.lastName}
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="bg-stone-800 border-orange-500/30 text-white focus:border-orange-500"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-white mb-2 block">
                  {t.contact.form.email}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-stone-800 border-orange-500/30 text-white focus:border-orange-500"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-white mb-2 block">
                  {t.contact.form.phone}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-stone-800 border-orange-500/30 text-white focus:border-orange-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="huntType" className="text-white mb-2 block">
                    {t.contact.form.huntType}
                  </Label>
                  <Select
                    value={formData.huntType}
                    onValueChange={(value) => setFormData({ ...formData, huntType: value })}
                  >
                    <SelectTrigger className="bg-stone-800 border-orange-500/30 text-white">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(t.contact.form.huntTypes).map((key) => (
                        <SelectItem key={key} value={key}>
                          {t.contact.form.huntTypes[key]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="guests" className="text-white mb-2 block">
                    {t.contact.form.guests}
                  </Label>
                  <Input
                    id="guests"
                    name="guests"
                    type="number"
                    min="1"
                    value={formData.guests}
                    onChange={handleChange}
                    required
                    className="bg-stone-800 border-orange-500/30 text-white focus:border-orange-500"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="dates" className="text-white mb-2 block">
                  {t.contact.form.dates}
                </Label>
                <Input
                  id="dates"
                  name="dates"
                  type="text"
                  placeholder="Ex: Octobre 2025"
                  value={formData.dates}
                  onChange={handleChange}
                  required
                  className="bg-stone-800 border-orange-500/30 text-white focus:border-orange-500"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-white mb-2 block">
                  {t.contact.form.message}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  className="bg-stone-800 border-orange-500/30 text-white focus:border-orange-500"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 text-lg transition-all duration-300 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send className="mr-2 h-5 w-5" />
                {isSubmitting ? 'Envoi…' : t.contact.form.submit}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
